import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import { Grid, Tooltip, Typography } from '@mui/material';
import {
  EmailField,
  FunctionField,
  Labeled,
  RecordContextProvider,
  SimpleShowLayout,
  TextField,
  useGetOne,
} from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { UserDetails } from 'src/models';
import { Spacing } from 'src/styles';

import { LockUserButton } from './LockUserButton';
import { UnlockUserButton } from './UnlockUserButton';
import { UpdateUserMfaPreferencesButton } from './UpdateUserMfaPreferencesButton';
import { UserAccountList } from './UserAccountList';

interface UserDetailsTabProps {
  id: string;
}

export const UserDetailsTab = ({ id }: UserDetailsTabProps) => {
  const { data: record } = useGetOne<UserDetails>(DataProviderResource.Users, {
    id,
  });

  const rootAccountId = record?.accounts?.find(account => account.isRoot)?.id;

  return (
    <RecordContextProvider value={record}>
      <Grid container direction='row'>
        <Grid item xs={12}>
          <SimpleShowLayout>
            <Typography variant='h5'>User Information</Typography>
          </SimpleShowLayout>
        </Grid>
        {record?.isClosed && (
          <Grid item xs={12}>
            <SimpleShowLayout>
              <Typography variant='subtitle1' color='error'>
                User has been permanently closed.
              </Typography>
            </SimpleShowLayout>
          </Grid>
        )}
        <Grid item md={12}>
          <Grid container direction='row'>
            <Grid item md={2}>
              <SimpleShowLayout>
                <TextField source='firstName' label='First Name' />
              </SimpleShowLayout>
            </Grid>
            <Grid item md={1}>
              <SimpleShowLayout>
                <TextField source='middleName' label='Middle Name' />
              </SimpleShowLayout>
            </Grid>
            <Grid item md={2}>
              <SimpleShowLayout>
                <TextField source='lastName' label='Last name' />
              </SimpleShowLayout>
            </Grid>
            <Grid item md={1}>
              <SimpleShowLayout>
                <TextField source='suffix' label='Suffix' />
              </SimpleShowLayout>
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={6}>
          <SimpleShowLayout>
            <EmailField source='email' label='Email' />
          </SimpleShowLayout>
        </Grid>
        <Grid item md={6}>
          <SimpleShowLayout>
            <TextField source='phoneNumber' label='Phone Number' />
          </SimpleShowLayout>
        </Grid>
        <Grid item md={6}>
          <SimpleShowLayout>
            <TextField source='signUpStatus' label='Sign Up Status' />
          </SimpleShowLayout>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Is MFA Enabled?'
                  render={(user: UserDetails) => {
                    return (
                      <Grid container direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Grid item className={Spacing.mt8}>
                          <Grid container direction='row' alignItems='center'>
                            <span>{user.isMfaEnabled ? <CheckIcon /> : <ClearIcon />}</span>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <UpdateUserMfaPreferencesButton id={user.id} enable />
                        </Grid>
                        <Grid item>
                          <UpdateUserMfaPreferencesButton id={user.id} />
                        </Grid>
                      </Grid>
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
          <Grid item md={6}>
            <SimpleShowLayout>
              <Labeled>
                <FunctionField
                  label='Is Locked?'
                  render={(user: UserDetails) => {
                    return (
                      <Grid container direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Grid item className={Spacing.mt8}>
                          <Grid container direction='row' alignItems='center'>
                            <span>{user.isLocked ? <CheckIcon /> : <ClearIcon />}</span>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <LockUserButton id={user.id} />
                        </Grid>
                        <Grid item>
                          <UnlockUserButton id={user.id} />
                        </Grid>
                      </Grid>
                    );
                  }}
                />
              </Labeled>
            </SimpleShowLayout>
          </Grid>
        </Grid>
        <Grid item md={6}>
          <SimpleShowLayout>
            <Labeled>
              <FunctionField
                label='Reset password attempts'
                render={(record: UserDetails) => {
                  return (
                    <Grid container direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                      <Grid item className={Spacing.mt8}>
                        <Grid container direction='row' alignItems='center'>
                          <Typography variant='body1' className={Spacing.mr4}>
                            {record.resetPasswordAttempts}
                          </Typography>
                          <Tooltip title='Maximum number of reset password attempts is 4.'>
                            <InfoIcon fontSize='small' color='info' />
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                }}
              />
            </Labeled>
          </SimpleShowLayout>
        </Grid>
        {record && (
          <Grid item md={12}>
            <UserAccountList
              user={record}
              accounts={record.accounts}
              rootAccountId={rootAccountId}
              showIsPrimary
              canLink
              canMove
            />
          </Grid>
        )}
        {record && (
          <Grid item md={12}>
            <UserAccountList
              title='Additional Accounts'
              user={record}
              accounts={record.accountLinks}
              rootAccountId={rootAccountId}
              canUnlink
            />
          </Grid>
        )}
      </Grid>
    </RecordContextProvider>
  );
};
